import { Form, Formik } from 'formik';
import React from 'react';
import config from 'react-global-configuration';
import {
  FormTextField,
  FormRadioButtons,
  FormTextAreaField,
  FormFileUpload,
  JourneyOverlay,
  Dialog,
  Callout,
  FormCheckbox,
} from '@hvcgroep/design-system';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';
import { useDialogHelpers } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink } from '@uncinc/react-drupal-core';
import { useSelector } from 'react-redux';
import { Container, Button } from '../../components';
import { JOB_APPLICATION_URL, routes } from '../../config/constants';
import { NodeVacancyEntity } from '../../types/nodes';

const JobApplicationFormScreen: React.FC = () => {
  const { t } = useTranslation();
  const [isError, setIsError] = React.useState(false);

  const API_URL = config.get('API_URL') as string;

  const referrer = useSelector(
    (state: Immutable.Map<string, string>) => state.getIn(['funnel', 'referrer']),
  ) as string | null;

  /**
   * Helper function so that we don't have to constantly
   * type the entire translation key
   */
  const formT = (key: string) => t(`job_application_form.fields.${key}`);

  const history = useHistory();

  const location = useLocation<{
    entity?: NodeVacancyEntity
  }>();

  const entity = location.state?.entity;

  const vacancyDetailsPath = location.pathname.split('/').slice(0, -1).join('/');

  const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] = React.useState(false);

  const confirmCloseDialogRef = React.useRef<HTMLDivElement>(null);

  const {
    dialogHelpersProps: confirmCloseDialogProps,
  } = useDialogHelpers({
    closeFunction: () => setIsConfirmCloseDialogOpen(false),
    wrapperElement: confirmCloseDialogRef,
    disableClosers: false,
    isOpen: isConfirmCloseDialogOpen,
  });

  if (!entity) {
    history.replace(vacancyDetailsPath);
    return null;
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(formT('first_name.required')),
    last_name: Yup.string().required(formT('last_name.required')),
    email: Yup.string().email(formT('email.email')).required(formT('email.required')),
    phone: Yup.string().required(formT('phone.required')),
    message: Yup.string(),
    cv: Yup.array().required().min(1, formT('cv.required')),
    cover_letter: Yup.array(),
    has_documents: Yup.string().required(formT('has_documents.required')).oneOf(['true', 'false'], formT('has_documents.required')),
    agree_to_terms: Yup.bool().isTrue(formT('agree_to_terms.required')),
  });

  return (
    <>
      <JourneyOverlay
        onCloseButtonClick={() => setIsConfirmCloseDialogOpen(true)}
      >
        <Container
          small
          className="JobApplicationForm px-0"
          fluid={false}
          large={false}
        >
          {isError && (
          <Callout className="u-margin--bottom--6">
            <h2 className="h3">
              {t('job_application_form.error.title')}
            </h2>
            <p>
              {t('job_application_form.error.content')}
            </p>
          </Callout>
          )}
          <h1>{t('job_application_form.title')}</h1>
          <p>{t('job_application_form.content')}</p>
          <Formik
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => {
              try {
                setIsError(false);

                helpers.setSubmitting(false);

                const formData = new FormData();

                formData.append('job_id', entity.field_jobylon_id[0].value);

                Object.entries(values).forEach(([key, value]) => {
                  if (typeof value === 'string' && value !== '') {
                    formData.append(key, value);
                  }

                  if (Array.isArray(value) && value[0] instanceof File) {
                    const file = value[0];
                    formData.append(key, file, file.name);
                  }
                });

                if (referrer) {
                  formData.append('original_referrer', referrer);
                }

                fetch(`${API_URL}${JOB_APPLICATION_URL}`, {
                  method: 'POST',
                  body: formData,
                })
                  .then((res) => {
                    return res.json();
                  })
                  .then((data: unknown) => {
                    if (typeof data !== 'object' || data == null || !('id' in data)) {
                      throw new Error('Unexpected payload - ID not present');
                    }

                    history.push(routes.applicationSent);

                    window.scrollTo(0, 0);
                  })
                  .catch((err) => {
                    console.error(err);
                    helpers.setSubmitting(false);
                    setIsError(true);
                    setTimeout(() => {
                      document.getElementsByClassName('JourneyOverlay__scroll-to-top')[0]?.scrollIntoView({ behavior: 'smooth' });
                    }, 0);
                  });
              } catch (err) {
                setIsError(true);
                console.error(err);
              }
            }}
            initialValues={{
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              cv: [] as File[],
              cover_letter: [] as File[],
              message: '',
              has_documents: '',
              agree_to_terms: false,
            }}
          >
            {(formik) => {
              return (
                <Form>
                  <FormTextField
                    name="first_name"
                    label={formT('first_name.label')}
                    placeholder={formT('first_name.placeholder')}
                  />
                  <FormTextField
                    name="last_name"
                    label={formT('last_name.label')}
                    placeholder={formT('last_name.placeholder')}
                  />
                  <FormTextField
                    name="email"
                    label={formT('email.label')}
                    placeholder={formT('email.placeholder')}
                  />
                  <FormTextField
                    name="phone"
                    label={formT('phone.label')}
                    placeholder={formT('phone.placeholder')}
                  />
                  <FormFileUpload
                    name="cv"
                    label={formT('cv.label')}
                    buttonText={t('form.field.file_upload.button')}
                    maxFileCount={1}
                    allowedFileMimes={['application/pdf', 'application/doc', 'application/docx', 'image/jpeg', 'image/jpg', 'image/png']}
                  />
                  <FormFileUpload
                    name="cover_letter"
                    label={formT('cover_letter.label')}
                    buttonText={t('form.field.file_upload.button')}
                    maxFileCount={1}
                    allowedFileMimes={['application/pdf', 'application/doc', 'application/docx', 'image/jpeg', 'image/jpg', 'image/png']}
                    optionalLabel
                  />
                  <FormTextAreaField
                    name="message"
                    label={formT('message.label')}
                    placeholder={formT('message.placeholder')}
                  />
                  <FormRadioButtons
                    name="has_documents"
                    label={formT('has_documents.label')}
                    options={[
                      {
                        value: formT('has_documents.options.true'),
                        key: 'true',
                      },
                      {
                        value: formT('has_documents.options.false'),
                        key: 'false',
                      },
                    ]}
                  />
                  <FormCheckbox
                    name="agree_to_terms"
                    label={(
                      <p className="text-bold">
                        <Trans t={t} i18nKey="job_application_form.fields.agree_to_terms.label">Ik ga akkoord met de<CoreLink to="/privacy">privacyverklaring</CoreLink></Trans>
                      </p>
                    )}
                  />
                  <Button
                    htmlType="submit"
                    variation="center"
                    className="u-margin--top--4"
                    aria-disabled={formik.isSubmitting}
                  >
                    {t('job_application_form.submit')}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Container>
      </JourneyOverlay>
      {isConfirmCloseDialogOpen && (
        <Dialog
          {...confirmCloseDialogProps}
          wrapperRef={confirmCloseDialogRef}
          labelId="confirm-close-journey"
          title={t('job_application_form.confirm_close_dialog.title')}
          primaryButton={{
            text: t('job_application_form.confirm_close_dialog.primary_button'),
            onClick: () => history.push(vacancyDetailsPath),
          }}
          secondaryButton={{
            text: t('job_application_form.confirm_close_dialog.secondary_button'),
            onClick: () => setIsConfirmCloseDialogOpen(false),
          }}
        >
          {t('job_application_form.confirm_close_dialog.content')}
        </Dialog>
      )}
    </>
  );
};

export default JobApplicationFormScreen;
